import {
    Box,
    ClickAwayListener,
    Stack,
    StackProps,
    Tooltip,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import HelpIcon from '@mui/icons-material/Help'
import { ReactNode, useMemo, useState } from 'react'
import Error from '@mui/icons-material/Error'
import Warning from '@mui/icons-material/Warning'

interface HelperTooltipProps extends StackProps {
    /**
     * helper text when hover ? icon
     */
    helperText?: ReactNode
    /**
     * display text when hover children component
     */
    descriptionText?: string
    children?: React.ReactNode
    helpIconSize?: number
    hideHelpIcon?: boolean
    iconAlignment?: 'start' | 'end'
    spacing?: number | string
    isErrorIcon?: boolean
    isWarningIcon?: boolean
    placement?:
        | 'bottom-end'
        | 'bottom-start'
        | 'bottom'
        | 'left-end'
        | 'left-start'
        | 'left'
        | 'right-end'
        | 'right-start'
        | 'right'
        | 'top-end'
        | 'top-start'
        | 'top'
}
const HelpTooltipIcon = (props: {
    helperText: ReactNode
    helpIconSize?: number
    handleTooltipOpen: () => void
    handleTooltipClose: () => void
    open: boolean
    isErrorIcon: boolean
    isWarningIcon: boolean
    placement:
        | 'bottom-end'
        | 'bottom-start'
        | 'bottom'
        | 'left-end'
        | 'left-start'
        | 'left'
        | 'right-end'
        | 'right-start'
        | 'right'
        | 'top-end'
        | 'top-start'
        | 'top'
}) => {
    const {
        open,
        handleTooltipOpen,
        handleTooltipClose,
        helperText,
        helpIconSize,
        placement,
        isErrorIcon,
        isWarningIcon,
    } = props

    const HelpToolIcon = isErrorIcon
        ? Error
        : isWarningIcon
        ? Warning
        : HelpIcon

    return (
        <Tooltip
            arrow
            PopperProps={{
                disablePortal: true,
            }}
            title={helperText}
            placement={placement}
            onMouseEnter={handleTooltipOpen}
            onMouseLeave={handleTooltipClose}
            onClick={handleTooltipOpen}
            open={open}
        >
            <Box
                px={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <HelpToolIcon
                    sx={{
                        color: isErrorIcon
                            ? 'error.main'
                            : isWarningIcon
                            ? 'warning.main'
                            : 'bluegrey.main',
                        fontSize: helpIconSize || 18,
                    }}
                />
            </Box>
        </Tooltip>
    )
}

function HelperTooltip(props: HelperTooltipProps) {
    const {
        helperText = '',
        children,
        helpIconSize = 18,
        iconAlignment = 'end',
        descriptionText = '',
        hideHelpIcon = false,
        placement = 'top-start',
        isErrorIcon = false,
        isWarningIcon = false,
        ...otherProps
    } = props

    const { t } = useTranslation()
    const [open, setOpen] = useState<boolean>(false)
    const [openDes, setOpenDes] = useState<boolean>(false)
    const title = useMemo(() => {
        if (typeof helperText === 'string') return t(helperText)
        return helperText
    }, [helperText, t])

    const helpTooltipIcon = useMemo(() => {
        if (hideHelpIcon) {
            return <></>
        } else {
            return (
                <HelpTooltipIcon
                    helpIconSize={helpIconSize}
                    helperText={title}
                    handleTooltipOpen={() => {
                        setOpen(true)
                    }}
                    handleTooltipClose={() => {
                        setOpen(false)
                    }}
                    open={open}
                    placement={placement}
                    isErrorIcon={isErrorIcon}
                    isWarningIcon={isWarningIcon}
                />
            )
        }
    }, [
        hideHelpIcon,
        helpIconSize,
        title,
        open,
        placement,
        isErrorIcon,
        isWarningIcon,
    ])

    return (
        <ClickAwayListener
            onClickAway={() => {
                setOpenDes(false)
                setOpen(false)
            }}
        >
            <Stack
                direction="row"
                alignItems="start"
                justifyContent="start"
                {...otherProps}
            >
                {helperText && iconAlignment === 'start' && helpTooltipIcon}
                <Tooltip
                    arrow
                    title={descriptionText}
                    placement={placement}
                    open={openDes}
                    onMouseEnter={() => setOpenDes(true)}
                    onMouseLeave={() => setOpenDes(false)}
                    onClick={() => setOpenDes((a) => !a)}
                >
                    <Box>{children}</Box>
                </Tooltip>
                {helperText && iconAlignment === 'end' && helpTooltipIcon}
            </Stack>
        </ClickAwayListener>
    )
}

export default HelperTooltip
